import { DiffFile, DiffModeEnum, DiffView, highlighter } from '@git-diff-view/react'
import '@git-diff-view/react/styles/diff-view.css'
import { useTheme } from '@pixee/design-system'
import { useMemo } from 'react'
import { inferLanguageFromFilename } from '../../utils/finding-utils.ts'
import './changeset-diff-view-overrides.css.ts'

/**
 * Wraps raw diff lines (prefixed with +/-/space) into a full unified diff string
 * with diff --git, ---/+++ file headers, and @@ hunk header so that @git-diff-view can parse them.
 */
const wrapPreviewAsUnifiedDiff = (preview: string, filePath: string): string[] => {
  const lines = preview.split('\n')
  const diffLines = lines.at(-1) === '' ? lines.slice(0, -1) : lines

  let oldCount = 0
  let newCount = 0
  for (const line of diffLines) {
    if (line.startsWith('-')) {
      oldCount++
    } else if (line.startsWith('+')) {
      newCount++
    } else {
      oldCount++
      newCount++
    }
  }

  const header = [
    `diff --git a/${filePath} b/${filePath}`,
    `--- a/${filePath}`,
    `+++ b/${filePath}`,
    `@@ -1,${oldCount} +1,${newCount} @@`,
  ].join('\n')

  return [`${header}\n${diffLines.join('\n')}`]
}

/**
 * Pre-creates a DiffFile with syntax highlighting already applied.
 * This bypasses the library's internal useEffect chain which has a condition bug
 * that can skip initSyntax() on re-renders.
 */
const createDiffFileWithSyntax = (
  preview: string,
  filePath: string,
  lang: string,
  theme: 'light' | 'dark'
): DiffFile => {
  const hunks = wrapPreviewAsUnifiedDiff(preview, filePath)
  const diffFile = new DiffFile(filePath, '', filePath, '', hunks, lang, lang)
  diffFile.initTheme(theme)
  diffFile.initRaw()
  diffFile.buildSplitDiffLines()
  diffFile.buildUnifiedDiffLines()
  diffFile.initSyntax({ registerHighlighter: highlighter })
  return diffFile
}

export const ChangesetDiffView: React.FC<{
  preview: string
  filePath: string
}> = ({ preview, filePath }) => {
  const { theme } = useTheme()
  const lang = inferLanguageFromFilename(filePath)
  const diffViewTheme = theme === 'dark' ? 'dark' : ('light' as const)

  const diffFile = useMemo(
    () => createDiffFileWithSyntax(preview, filePath, lang, diffViewTheme),
    [preview, filePath, lang, diffViewTheme]
  )

  return (
    <DiffView
      diffFile={diffFile}
      diffViewMode={DiffModeEnum.Unified}
      diffViewTheme={diffViewTheme}
      diffViewHighlight={true}
      diffViewWrap={true}
      diffViewFontSize={14}
    />
  )
}
